// externals
import { useRef } from 'react';
import { useSelector } from 'react-redux';

// libraries
import { Typography } from '@interstate/components/Typography';
import { useScrollIntoView } from '@makemydeal/dr-dash-ui-utils';

// selectors
import * as purchaseHistorySelectors from '@makemydeal/dr-dash-store/selectors/purchaseHistorySelectors';

// components
import PurchaseHistorySectionUI from './PurchaseHistorySectionUI';
import PurchaseHistorySkeleton from './PurchaseHistorySkeleton';

// styles
import { StyledLeadingNote, StyledMessage, StyledSecondaryNote } from './PurchaseHistorySection.style';

/** The skeleton alone carries no text, which reads as an empty panel rather than as work happening. */
export const PURCHASE_HISTORY_RETRIEVING_MESSAGE = 'Retrieving purchase history…';

/** Must stay clearly distinguishable from the unavailable message: "still working" is not "failed". */
export const PURCHASE_HISTORY_TAKING_LONGER_MESSAGE = 'This is taking longer than usual.';

export const PurchaseHistorySection = () => {
    const sectionState = useSelector(purchaseHistorySelectors.getPurchaseHistorySectionState);
    const uniqueScrollRequestId = useSelector(purchaseHistorySelectors.getPurchaseHistoryScrollRequestId);
    const sectionRef = useRef<HTMLDivElement>(null);

    // Re-triggered by a changing request id, since the section is usually already mounted.
    useScrollIntoView(sectionRef, uniqueScrollRequestId);

    const renderContent = () => {
        switch (sectionState.status) {
            case 'loading':
                return (
                    <>
                        <StyledLeadingNote>
                            <Typography
                                tag="p"
                                variant="body-sm"
                                color="sem.color.on-surface.muted"
                                data-testid="purchase-history-retrieving"
                            >
                                {PURCHASE_HISTORY_RETRIEVING_MESSAGE}
                            </Typography>
                        </StyledLeadingNote>
                        {/* The skeleton REMAINS — replacing it would read as a failure, not a delay. */}
                        <PurchaseHistorySkeleton />
                        {sectionState.isTakingLonger && (
                            <StyledSecondaryNote>
                                <Typography
                                    tag="p"
                                    variant="body-sm"
                                    color="sem.color.on-surface.muted"
                                    data-testid="purchase-history-taking-longer"
                                >
                                    {PURCHASE_HISTORY_TAKING_LONGER_MESSAGE}
                                </Typography>
                            </StyledSecondaryNote>
                        )}
                    </>
                );

            case 'ready':
                return <PurchaseHistorySectionUI records={sectionState.records} />;

            case 'empty':
                return (
                    <StyledMessage>
                        <Typography
                            tag="p"
                            variant="body-sm"
                            color="sem.color.on-surface.muted"
                            data-testid="purchase-history-empty"
                        >
                            No prior purchase history at this dealership
                        </Typography>
                    </StyledMessage>
                );

            case 'unavailable':
                return (
                    <StyledMessage>
                        <Typography
                            tag="p"
                            variant="body-sm"
                            color="sem.color.on-surface.muted"
                            data-testid="purchase-history-unavailable"
                        >
                            We couldn&apos;t load purchase history right now.
                        </Typography>
                    </StyledMessage>
                );
        }
    };

    // A polite live region so a late result is announced rather than appearing silently. On a wrapper
    // because Interstate's Typography does not accept ARIA props.
    return (
        <div ref={sectionRef} role="status" aria-live="polite" data-testid="purchase-history-live-region">
            {renderContent()}
        </div>
    );
};

export default PurchaseHistorySection;
